import React from 'react';
import { ListColumnType, ListFilterFieldType, ListFilterType, ListMoreMenuConfig } from 'types/ListTypes';
import { BulkActionsConfig } from 'types/BulkActionTypes';
import AbstractList from 'components/lists/AbstractList';
import ProposalModal from './ProposalModal';
import { useSettings } from 'services/settings/SettingsContext';
import { renderDate, renderDocumentNumber, renderPriceDecimalColumn, renderStatusBadge } from 'components/lists/functions/renderListColumn';

// Set the filters for the proposal detail list, the main proposal list uses the filter fields below
export const proposalListFilters: ListFilterType[] = [
    {
        id: 'active',
        label: 'proposal.general.all_proposals_filter_label',
        params: { 'deleted': false },
        default: true,
    },
    {
        group: 'proposal.general.status_filter_group_label',
        filters: [
            {
                id: 'draft',
                label: 'proposal.status.draft',
                params: { 'status': 'draft' },
            },
            {
                id: 'sent',
                label: 'proposal.status.sent',
                params: { 'status': 'sent' },
            },
            {
                id: 'expired',
                label: 'proposal.status.expired',
                params: { 'status': 'expired' },
            },
            {
                id: 'accepted',
                label: 'proposal.status.accepted',
                params: { 'status': 'accepted' },
            },
            {
                id: 'rejected',
                label: 'proposal.status.rejected',
                params: { 'status': 'rejected' },
            },
        ]
    },
    {
        group: 'general.archive',
        filters: [
            {
                id: 'deleted',
                label: 'proposal.general.deleted_proposals_label',
                shortName: 'general.deleted',
                params: { 'deleted': true },
            }
        ]
    }
]

// Enable the bulk actions panel for this list
const proposalBulkActions: BulkActionsConfig = {
    objectKey: 'proposal',
    deleteOrDeactivate: 'delete',
    showExport: true,
};

// Configure the more menu of this list
const proposalMoreMenu: ListMoreMenuConfig = {
    inactiveItemsToggle: {
        semantic: 'deleted',
        activeParams: { deleted: true },
        clearFilterNames: ['status'],
    },
};

// Set the filter fields of this list
const proposalFilterFields: ListFilterFieldType[] = [
    {
        type: 'search',
        name: 'search',
        label: 'forms.search_label',
        placeholder: 'forms.search_placeholder',
        queryParam: 'search',
        width: 1.5
    },
    {
        type: 'dropdown',
        name: 'workflow',
        label: 'lists.filters.type_label',
        queryParam: 'workflow',
        apiRequest: {
            endpoint: 'get_workflow_list',
            params: { connected_feature: 'proposal', is_active: true },
            object: 'workflow'
        },
        groupByField: 'division',
        width: 1.25
    },
    {
        type: 'dropdown',
        name: 'status',
        label: 'lists.filters.status_label',
        queryParam: 'status',
        backendField: 'id',
        translationPrefix: 'proposal.status.',
        optionField: 'id',
        hardcodedOptions: [
            { id: 'draft' },
            { id: 'sent' },
            { id: 'expired' },
            { id: 'accepted' },
            { id: 'rejected' },
        ],
        width: 1.25
    },
    {
        type: 'daterange',
        name: 'proposal_date_range',
        label: 'proposal.general.date_label',
        queryParam: 'proposal_date',
        width: 2
    },
    {
        type: 'dropdown',
        name: 'division',
        label: 'lists.filters.division_label',
        queryParam: 'division',
        requiredFeature: 'divisions',
        defaultValueFromContext: 'user_division',
        apiRequest: {
            endpoint: 'get_division_list',
            params: { is_active: true },
            object: 'division'
        },
        width: 1.5
    }
];

// Rename the name of the function:
const ProposalList: React.FC = () => {
    const { userLocale } = useSettings();

    // Specify the options of this list:
    const query = {
        endpoint: 'get_proposal_list',
        defaultParams: { 'deleted': false },
        object: 'proposal'
    }
    const objectName = 'proposal.general.object_name'

    // Set isPaginated to true if this list may have 100.000+ items:
    const isPaginated = true

    // Specify form, detail modal or detailpage for this list
    const postNewItem = { url: 'post_proposal' }

    const detailModal = <ProposalModal />;
    const onRowClick = 'detailModal'  

    // Specify the columns of this list
    const columns: ListColumnType[] = [
        { 
            width: '1.5fr', 
            field: 'number', 
            label: 'proposal.general.proposal_number_label', 
            render: (_, row) => renderDocumentNumber(row.number, row.status, 'proposal') 
        },
        { 
            width: '3fr', 
            field: 'internal_reference', 
            label: 'proposal.general.internal_reference_label' 
        },
        { 
            width: '2fr', 
            field: 'proposal_date', 
            label: 'proposal.general.date_label', 
            render: (_, row) => renderDate(row.proposal_date, userLocale) 
        },
        { 
            width: '4fr', 
            field: 'recipient_name', 
            orderField: 'recipient__name',
            label: 'proposal.general.recipient_label' 
        },
        { 
            width: '1.5fr', 
            field: 'amount', 
            label: 'proposal.general.amount_label', 
            render: (_, row) => renderPriceDecimalColumn(row.amount, row.currency, userLocale) 
        },
        { 
            width: '144px', 
            field: 'status', 
            label: 'proposal.general.status_label', 
            disableOrdering: true,
            render: (_, row) => renderStatusBadge(row.status) 
        }
    ];

    return (
        <AbstractList
            query={query}
            objectName={objectName}
            columns={columns}
            filterFields={proposalFilterFields}
            postNewItem={postNewItem}
            detailModal={detailModal}
            isPaginated={isPaginated}
            onRowClick={onRowClick}
            bulkActions={proposalBulkActions}
            moreMenu={proposalMoreMenu}
        />
    );
};

export default ProposalList;